import CardCommon from '@op/components/CardCommon';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { useKeyMetricsCommunityPieChart } from '../../hooks';
import { CommunityOptionsType, KeyMetricsType } from '../constants';
import EChartCommon from '@op/components/EChartCommon';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { NO_DATE } from '@op/constants';
import { echartsContent, getPercentageInfo, mathFormat } from '@op/utils';
import { reviewsFormat } from '@op/utils/value';
import {
  barIcon,
  lineIcon,
  pieIcon,
  stackLineIcon,
  SymbolType,
  tableIcon,
} from '@op/constants/chart';
import { getChartsGradientColor, getXAxisAxisLabel } from '@op/utils/chart';
import { ServerTimeType } from '@op/components/FilterCommon/types';
import Table from './Table';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { formatTimeToShow } from '@op/utils/time';
import { DBO_V, DBO_Y } from '@op/utils/format';

const chartConfig = [
  {
    tab: 'earned',
    key: 'earned',
    name: 'Earned',
    header: 'Earned',
    itemStyle: { color: '#5086F3' },
  },
  {
    tab: 'official_accounts',
    key: 'official_account',
    name: 'Official Account-1',
    header: 'Official Account-1',
    text: 'Official Account', /// 下载文件标题
    itemStyle: { color: '#69D19C' },
  },
];

export default function RightChart({
  filter,
  tab,
  metrics,
  getOtherParams,
  isPercent = false,
  formatValue = DBO_V.reviews,
}: {
  filter: any;
  tab: CommunityOptionsType;
  metrics: {
    value: KeyMetricsType;
    format?: (value: number) => number | string;
  };
  getOtherParams: (tab: CommunityOptionsType) => any;
  isPercent?: boolean;
  formatValue?: (value: number) => number | string;
}) {
  const { formatMessage } = useIntl();
  const { download, downloading } = useDownloadParams();
  const eChartsRef = React.useRef(null);
  const [chartType, setChartType] = useState<SymbolType>('pie');
  const chartRealType: any = chartType === 'stack_line' ? 'line' : chartType;
  const params = useMemo(
    () => ({
      ...filter,
      metrics: [metrics.value],
      tab_type: tab,
      games: [],
      ...getOtherParams(tab),
    }),
    [filter, metrics],
  );

  const { config, operations } = useMemo(() => {
    let operations = [pieIcon, tableIcon, lineIcon, stackLineIcon, barIcon];
    let config = chartConfig;
    if (tab !== 'official_accounts' && !isPercent) {
      return { config, operations };
    }

    setChartType('bar');
    const filterOperations = [pieIcon.symbol];
    if (tab === 'official_accounts') {
      config = config.filter((val) => val.tab === tab);
    }
    if (isPercent) {
      filterOperations.push(stackLineIcon.symbol);
    }
    operations = operations.filter(
      (item) => !filterOperations.includes(item.symbol),
    );

    return {
      config,
      operations,
    };
  }, [tab, isPercent]);

  const { data, loading } = useKeyMetricsCommunityPieChart({
    filter: params,
    extraData: config,
  });

  const createPieChart = (dateType: ServerTimeType) => {
    const total = data.pieTotalData || 0;
    return {
      tooltip: {
        className: 'dbo-mentions-sentiment-box-tooltip-wrapper',
        enterable: true,
        formatter: (params: any) => {
          const leftTitle = formatTimeToShow(filter);
          const rightTitle = formatMessage({ id: NO_DATE.EMPTY });
          return formatChartParamsDate({
            title: leftTitle,
            rightTitle,
            params,
            listTitleFunc: (item) => formatMessage({ id: item.name }),
            valueFormat: (value) => {
              const percent = getPercentageInfo(params?.value, total);
              return `${isNaN(value) ? '-' : reviewsFormat(value)}(${percent})`;
            },
          });
        },
      },
      series: [
        {
          type: 'pie',
          data: data.pieData,
          alignTo: 'edge',
          label: {
            color: '#262832',
            formatter: (params: any) => {
              const percent = total
                ? getPercentageInfo(params?.value, total, 2)
                : `${params.percent.toFixed(2)}%`;
              return `{value| ${formatMessage({
                id: params.name,
              })}:}\n{a|${mathFormat(params.value)}(${percent})}\n{hr|}\n{a|}`;
            },
            rich: {
              value: {
                align: 'center',
              },
              a: {
                // color: '#999',
                lineHeight: 20, // 设置最后一行空数据高度，为了能让延长线与hr线对接起来
                align: 'center',
              },
              hr: {
                // 设置hr是为了让中间线能够自适应长度
                borderColor: 'auto', // hr的颜色为auto时候会主动显示颜色的
                width: '105%',
                borderWidth: 0.5,
                height: 0.5,
              },
              per: {
                // 用百分比数据来调整下数字位置，显的好看些。如果不设置，formatter最后一行的空数据就不需要
                padding: [4, 0],
              },
            },
          },
          labelLayout: (params: any) => {
            const isLeft =
              params.labelRect.x <
              eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
            const points = params.labelLinePoints;
            const heightGap = params.labelRect.height - 32.5 - 10;
            // Update the end point.
            points[1][1] += heightGap > 0 ? heightGap : 0;
            points[2][1] += heightGap > 0 ? heightGap : 0;

            points[2][0] = isLeft
              ? params.labelRect.x
              : params.labelRect.x + params.labelRect.width;
            return {
              labelLinePoints: points,
            };
          },
          radius: '50%',
          emphasis: {
            focus: 'self',
            label: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
          blur: {
            label: { opacity: 0.2 },
            itemStyle: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
        },
      ],
      legend: {
        formatter: (name) => formatMessage({ id: name }),
      },
    };
  };

  const options = useMemo(() => {
    const dateType = filter.date_type;
    if (chartType === 'pie') {
      return createPieChart(dateType);
    }
    const series: any = [];
    config.forEach(({ key, header, itemStyle: { color } }) => {
      const item: any = {
        name: header,
        type: chartRealType,
        data: data.yAxisData?.[key],
        itemStyle: {
          color,
        },
      };
      if (['bar', 'stack_line'].includes(chartType)) {
        item.stack = 'Total';
        if (chartType === 'stack_line') {
          item.areaStyle = getChartsGradientColor(color, 0, 0.4);
        }
      }
      series.push(item);
    });

    return {
      series,
      legend: {
        data: config,
        formatter: (name) => formatMessage({ id: name }),
      },
      tooltip: {
        trigger: 'axis',
        className: 'dbo-mentions-sentiment-box-tooltip-wrapper',
        formatter: (params: any) => {
          const leftTitle = listTimeTitleFunc(params[0]?.axisValue, dateType);
          const rightTitle = formatMessage({ id: NO_DATE.EMPTY });
          return formatChartParamsDate({
            title: leftTitle,
            rightTitle,
            params,
            isSort: true,
            listTitleFunc: (item) => formatMessage({ id: item.seriesName }),
            valueFormat: (value: number) => formatValue(value),
          });
        },
      },
      xAxis: {
        axisLabel: getXAxisAxisLabel(filter),
        data: data?.xAxisData || [],
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) =>
              DBO_Y.reviewsPercent(value, isPercent, true),
            color: '#838697',
          },
          splitNumber: 5,
        },
      ],
      grid: {
        left: 0,
        right: 5,
      },
    };
  }, [filter, chartType, config, data, formatMessage]);

  const empty =
    chartType === 'pie' ? data.pieTotalData === 0 : data.timelineTotal === 0;

  return (
    <CardCommon
      contentCard
      title={formatMessage({ id: 'Official Account VS Earned' })}
      hasDownload
      downloadLoading={downloading}
      operations={operations}
      handleOperationsOnClick={(item: any) => {
        setChartType(item.symbol);
      }}
      operationInfo={[chartType, setChartType]}
      loading={loading}
      loadingHeight={338}
      empty={empty}
      emptyHeight={338}
      logBtnId="KMCMA"
      handleDownloadOnClick={() => {
        download({
          key: 'KeyMetricsOfficialAndEarned',
          fileName: 'Key Metrics Official Account & Earned',
          filter,
          auxiliaryParams: {
            columns: config || [],
            isPercent,
          },
          data: data.tableData,
        });
      }}
    >
      {chartType === 'table' ? (
        <Table
          columns={config}
          data={data.tableData}
          filter={filter}
          formatValue={formatValue}
        />
      ) : (
        <EChartCommon
          optionsProps={{ type: chartRealType }}
          height={374}
          options={options}
          ref={eChartsRef}
        ></EChartCommon>
      )}
    </CardCommon>
  );
}
